import React from "react";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import Section from "@/components/Section";
import { Link } from "react-router-dom";
import { ShieldCheck, Lock, FileText, Split } from "lucide-react";

const items = [
  {
    icon: <ShieldCheck size={20} strokeWidth={1.5} />,
    title: "Consent-first data capture",
    body: "Every form on DT6 captures consent posture. Records without an appropriate lawful basis are quarantined, not activated."
  },
  {
    icon: <Lock size={20} strokeWidth={1.5} />,
    title: "TCPA cell-flag audit",
    body: "Every phone universe passes through a cell-flag audit before SMS activation. Prior express written consent, revocation posture, and STOP/HELP handling are documented."
  },
  {
    icon: <FileText size={20} strokeWidth={1.5} />,
    title: "CAN-SPAM adherence",
    body: "Email sends observe CAN-SPAM including sender identification, physical postal address, and functional unsubscribe. Suppression lists are honored across the platform."
  },
  {
    icon: <Split size={20} strokeWidth={1.5} />,
    title: "State privacy posture",
    body: "We track CA (CCPA/CPRA), VA (VCDPA), CO (CPA), CT (CTDPA), UT (UCPA), and other state laws. Data-subject requests route to a documented DSAR workflow."
  },
  {
    icon: <Split size={20} strokeWidth={1.5} />,
    title: "IE / CE separation",
    body: "Independent-expenditure and coordinated-expenditure data flows are logically separated with role-gated access and audit logging."
  },
];

export default function CompliancePage() {
  return (
    <>
      <PublicNav />
      <Section className="pt-16 pb-8" testId="compliance-hero">
        <div className="dt6-chip dt6-chip-primary mb-4">/ COMPLIANCE POSTURE</div>
        <h1 className="font-heading text-5xl md:text-6xl font-black leading-tight">
          Compliance is a <span style={{ color: "var(--dt6-primary)" }}>system property</span>, not a checkbox.
        </h1>
        <p className="mt-4 text-lg text-[color:var(--dt6-muted)] max-w-3xl">
          DT6 is built compliance-first. Every module logs actions to an immutable audit trail. Consent and lawful basis are captured at intake. Enforcement is automatic, not aspirational.
        </p>
        <p className="mt-3 text-sm text-[color:var(--dt6-text)] max-w-3xl" data-testid="compliance-standard-line">
          Alignment to <a href="/standard" className="dt6-link">The DT6 Standard</a> is a condition of service. Access to the arsenal requires approval by a DT6 principal.
        </p>
      </Section>

      <Section className="py-8" testId="compliance-items">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((it, i) => (
            <div key={i} className="dt6-card p-6" data-testid={`compliance-item-${i}`}>
              <div className="text-[color:var(--dt6-primary)]">{it.icon}</div>
              <h3 className="mt-4 font-heading text-xl font-bold">{it.title}</h3>
              <p className="mt-2 text-sm text-[color:var(--dt6-muted)]">{it.body}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section className="py-12" testId="compliance-dsar">
        <div className="dt6-card p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="dt6-chip mb-3">/ DATA-SUBJECT REQUESTS</div>
            <h3 className="font-heading text-2xl font-bold">Access, delete, or correct your data.</h3>
            <p className="text-[color:var(--dt6-muted)] mt-2 max-w-2xl">
              We respond to verified DSAR requests within 30 days per applicable law. Submit via the form or via your account settings.
            </p>
          </div>
          <Link to="/dsar" className="dt6-btn dt6-btn-primary" data-testid="compliance-dsar-cta">Submit DSAR</Link>
        </div>
      </Section>

      <PublicFooter />
    </>
  );
}
